import { useCallback, useMemo, useRef, useState } from 'react';
import { View, Text, ActivityIndicator, useWindowDimensions, FlatList } from 'react-native-web';

import SportIconButton from './SportIconButton';
import ChevronLeftSrc from '../../../../images/icons/chevron-back-outline.svg';
import ChevronRightSrc from '../../../../images/icons/chevron-forward-outline.svg';
import SportIconArrow from './SportIconButton/SportIconArrow';
import useDiscoverStore, {
  DiscoverHelpers,
  DiscoverActions,
} from '../../../../zustand-stores/DiscoverStore';
import toggleArrayItem from '../../../../zustand-stores/DiscoverStore/utils/toggleArrayItem';
import responsiveStyle from '../../styles/styles';
import { useDiscoverTopSports, useDiscoverRentalSports } from '../../hooks';
import { useUrlParams } from '../../../../hooks';
import { SubTabEnum } from '../../../../shared/discover-tabs-enum';
import { DISCOVER_TOP_SPORTS_LIMIT } from '../../hooks/useDiscoverTopSports';
import { Tooltip } from '../../../../components';

const { applyViewFilters, applySportsFilter } = DiscoverHelpers;

const { updateFilterCollapsed } = DiscoverActions;

/**
 * TODO: Move to shared library
 * Top sports icon buttons
 * TODO: rely on organizationId rather than cityName
 */

/** @type React.FC  */
const TopSportsIconsFilter = () => {
  const sportsIconFilterRef = useRef(null);
  const [scrollAmt, setScrollAmt] = useState(0);
  const [showScrollLeftIcon, setShowScrollLeftIcon] = useState(false);
  const [showScrollRightIcon, setShowScrollRightIcon] = useState(true);

  const layout = useWindowDimensions();
  const styles = responsiveStyle(layout);

  const { updateUrlParams } = useUrlParams();

  const cityName = useDiscoverStore(store => store.filters.cityName);
  const selectedSportNames = useDiscoverStore(store => store.filters.selectedSportNames);
  const subView = useDiscoverStore(store => store.subView);
  const view = useDiscoverStore(store => store.view);

  const isRental = subView === SubTabEnum.BY_SPORT || subView === SubTabEnum.BY_VENUE;

  const isAllSportsView = subView === SubTabEnum.LEAGUE;

  const {
    sportNames: topSportNames,
    loading: topSportsLoading,
    error: topSportsError,
  } = useDiscoverTopSports(cityName);

  const {
    sportNames: rentalSportNames,
    loading: rentalSportsLoading,
    error: rentalSportsError,
  } = useDiscoverRentalSports(cityName, isRental);

  const limit = isAllSportsView ? topSportNames.length : DISCOVER_TOP_SPORTS_LIMIT;

  const sports = isRental ? applySportsFilter(rentalSportNames) : applySportsFilter(topSportNames);

  const handleScroll = ({ nativeEvent }) => {
    const { contentOffset, contentSize, layoutMeasurement } = nativeEvent;

    // Are we at the beginning of the list?
    if (contentOffset.x > 0) {
      setShowScrollLeftIcon(true);
    } else {
      setShowScrollLeftIcon(false);
    }

    // Are we at the end of the list?
    if (contentOffset.x + layoutMeasurement.width >= contentSize.width) {
      setShowScrollRightIcon(false);
    } else {
      setShowScrollRightIcon(true);
    }

    setScrollAmt(contentOffset.x);
  };

  const onPressSportIcon = useCallback(
    async sport => {
      const updatedSports = toggleArrayItem(selectedSportNames, sport);
      if (updatedSports.includes(sport)) updateFilterCollapsed('selectedSportNames', false);
      updateUrlParams({ sportNames: updatedSports });
    },
    [selectedSportNames, updateUrlParams]
  );

  const visibleSportNames = sports.slice(0, limit);
  const finalSportsData = applyViewFilters(view, visibleSportNames);

  // memo for tooltips
  const TooltipsMemo = useMemo(() => {
    return finalSportsData.map(sport => <Tooltip key={sport} id={sport} />);
  }, [finalSportsData]);

  if (topSportsLoading || rentalSportsLoading) {
    return <ActivityIndicator size="small" />;
  }

  if (topSportsError || rentalSportsError) {
    return <Text>{String(topSportsError || rentalSportsError)}</Text>;
  }

  return (
    <View style={{ position: 'relative' }}>
      {isAllSportsView && showScrollLeftIcon && (
        <SportIconArrow
          imageSource={{ uri: ChevronLeftSrc, height: 20, width: 20 }}
          variant="left"
          onPress={() => {
            // Ensure the scroll never goes below 0.
            const amountToScroll = Math.max(0, scrollAmt - 200);

            sportsIconFilterRef.current.scrollToOffset({
              offset: amountToScroll,
            });
            setScrollAmt(amountToScroll);
          }}
        />
      )}
      <FlatList
        data={finalSportsData}
        keyExtractor={sport => sport}
        ref={sportsIconFilterRef}
        horizontal
        initialNumToRender={limit}
        contentContainerStyle={[
          styles('sportIconsContainer'),
          isAllSportsView
            ? { justifyContent: 'flex-start' }
            : // Flex 1 is required here to stretch the container to fill available space
              { flex: 1, justifyContent: 'center' },
        ]}
        onScroll={handleScroll}
        renderItem={({ item: sport }) => (
          <div data-for={sport} data-tip={sport}>
            <SportIconButton
              sportName={sport}
              styles={styles('sportIcon')}
              onPress={onPressSportIcon}
              isPressed={selectedSportNames.includes(sport)}
            />
          </div>
        )}
      />
      {TooltipsMemo}
      {isAllSportsView && showScrollRightIcon && (
        <SportIconArrow
          imageSource={{ uri: ChevronRightSrc, height: 20, width: 20 }}
          variant="right"
          onPress={() => {
            const amountToScroll = scrollAmt + 200;

            sportsIconFilterRef.current.scrollToOffset({
              offset: amountToScroll,
            });
            setScrollAmt(amountToScroll);
          }}
        />
      )}
    </View>
  );
};

export default TopSportsIconsFilter;
